import React from 'react'

import { F0Box, F0Checkbox, F0Heading, F0Text } from '@factorialco/f0-react'
import {
  WizardStep as DeprecatedWizardStep,
  WizardStepRenderParams as DeprecatedWizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import TextField from 'modules/core/components/TextField'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { NetsuiteWizardData } from 'modules/integrations_netsuite/lib/types'

type NetSuiteWizardStep = DeprecatedWizardStep<NetsuiteWizardData>
type NetSuiteWizardStepRenderParams = DeprecatedWizardStepRenderParams<NetsuiteWizardData>

const GuideCard = ({
  step,
  title,
  description,
}: {
  step: string
  title: string
  description: string
}) => (
  <F0Box
    display="flex"
    flexDirection="row"
    gap="md"
    padding="lg"
    border="default"
    borderColor="secondary"
    borderRadius="md"
    background="primary"
  >
    <F0Box
      width="8"
      height="8"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="xl"
      background="info"
    >
      <F0Text variant="label" content={step} />
    </F0Box>
    <F0Box display="flex" flexDirection="column" gap="xs">
      <F0Text variant="label" content={title} />
      <F0Text variant="body" color="secondary" content={description} />
    </F0Box>
  </F0Box>
)

const ChecklistItem = ({ label, checked }: { label: string; checked: boolean }) => (
  <F0Box
    display="flex"
    flexDirection="row"
    alignItems="center"
    gap="sm"
    padding="sm"
    border="default"
    borderColor="secondary"
    borderRadius="md"
    background="primary"
  >
    <F0Checkbox title={label} hideLabel checked={checked} disabled onCheckedChange={() => {}} />
    <F0Text variant="body" color={checked ? 'default' : 'secondary'} content={label} />
  </F0Box>
)

const ClientIdStep = ({ data, onDataChanged }: NetSuiteWizardStepRenderParams) => {
  const session = useCurrentSession()

  useOnNext(() => {
    track(trackingEvents.integrationsNetsuite.netsuiteSettingsClientIdEntered, {
      company_id: session.company.id,
      access_id: session.access.id,
    })
  })

  const handleChange = (value: string) => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        client_id: value,
      },
    })
  }

  return (
    <F0Box display="flex" flexDirection="row" gap="2xl">
      <F0Box display="flex" flexDirection="column" gap="lg" maxWidth="screen-sm">
        <F0Box display="flex" flexDirection="column" gap="xs">
          <F0Heading
            as="h2"
            variant="heading"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.title'
            )}
          />
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.description'
            )}
          />
        </F0Box>
        <TextField
          label={i18n.t(
            'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.label'
          )}
          value={data.answers.client_id || ''}
          onChange={handleChange}
        />
      </F0Box>
      <F0Box width="96" display="flex" flexDirection="column" gap="md">
        <F0Box
          display="flex"
          flexDirection="column"
          gap="lg"
          padding="lg"
          background="secondary"
          borderRadius="md"
        >
          <F0Text
            variant="label"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.guide_title'
            )}
          />
          <GuideCard
            step="1"
            title={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.navigation_title'
            )}
            description={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.navigation_description'
            )}
          />
          <F0Box
            display="flex"
            flexDirection="column"
            gap="md"
            padding="lg"
            border="default"
            borderColor="secondary"
            borderRadius="md"
            background="primary"
          >
            <F0Box display="flex" flexDirection="column" gap="xs">
              <F0Text
                variant="label"
                content={i18n.t(
                  'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.oauth_title'
                )}
              />
              <F0Text
                variant="body"
                color="secondary"
                content={i18n.t(
                  'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.oauth_description'
                )}
              />
            </F0Box>
            <ChecklistItem
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.setting_restlets'
              )}
              checked
            />
            <ChecklistItem
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.setting_rest_web_services'
              )}
              checked
            />
            <ChecklistItem
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.setting_suiteanalytics_connect'
              )}
              checked
            />
            <ChecklistItem
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.setting_m2m'
              )}
              checked
            />
            <ChecklistItem
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.setting_authorization_code'
              )}
              checked={false}
            />
            <ChecklistItem
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.setting_token_based_authentication'
              )}
              checked={false}
            />
          </F0Box>
          <GuideCard
            step="3"
            title={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.copy_title'
            )}
            description={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.copy_description'
            )}
          />
        </F0Box>
      </F0Box>
    </F0Box>
  )
}

export const clientIdStep = (): NetSuiteWizardStep => ({
  title: i18n.t(
    'app_store_v2.app_integrations.integrations_netsuite.settings.steps.client_id.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <ClientIdStep {...props} />,
  isCompleted: (data) => !!data.answers.client_id,
})
